// Global quota-error interceptor.
//
// Subscribes to the QueryClient's mutationCache + queryCache and watches for
// PostgresError with sqlstate 'PC001' (raised by billing.fn_assert_quota when
// an org tries to exceed its plan). When detected, opens a centered modal
// linking to /settings/billing.
//
// The label shown to the user comes from the SQL `hint` payload (which is
// sourced from billing.quota_kinds.label), so adding a new quota kind in the
// admin requires zero changes here.

import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { parseQuotaError, type QuotaError } from '@pc/db';
import { Button, Modal } from '@pc/ui';

export function QuotaUpsellProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [err, setErr] = useState<QuotaError | null>(null);

  useEffect(() => {
    const onErr = (e: unknown) => {
      const q = parseQuotaError(e);
      if (q) setErr(q);
    };
    const mUnsub = qc.getMutationCache().subscribe((event) => {
      if (event.type === 'updated' && event.mutation.state.status === 'error') {
        onErr(event.mutation.state.error);
      }
    });
    const qUnsub = qc.getQueryCache().subscribe((event) => {
      if (event.type === 'updated' && event.query.state.status === 'error') {
        onErr(event.query.state.error);
      }
    });
    return () => {
      mUnsub();
      qUnsub();
    };
  }, [qc]);

  const labelTitle = err?.label ?? 'Plan';
  const labelLower = (err?.label ?? 'records').toLowerCase();

  return (
    <>
      {children}
      <Modal
        open={err !== null}
        onClose={() => setErr(null)}
        title={`${labelTitle} limit reached`}
      >
        {err ? (
          <div>
            <p className="text-sm text-ink-2">
              Your current plan caps {labelLower} at{' '}
              <strong className="tabular">{err.cap.toLocaleString()}</strong>. You&rsquo;ve used{' '}
              <strong className="tabular">{err.current.toLocaleString()}</strong>.
            </p>
            <p className="text-sm text-ink-2 mt-3">
              Upgrade your plan to continue. New {labelLower} are blocked until you upgrade or
              your plan&rsquo;s period resets.
            </p>
            <div className="mt-5 flex gap-3 justify-end">
              <Button variant="ghost" onClick={() => setErr(null)}>
                Dismiss
              </Button>
              <Link to="/settings/billing" onClick={() => setErr(null)}>
                <Button>Upgrade plan</Button>
              </Link>
            </div>
          </div>
        ) : null}
      </Modal>
    </>
  );
}
